import { AfterViewInit, ChangeDetectorRef, Component, effect, Input, OnInit } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import { AlarmOption, PatientTargetDataModel, PatientTargetModel, PatientTargetStatus } from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { AlertService } from '../../../shared/services/alert.service';
import { isOpenEndedFluencyRate as targetIsOpenEndedFluencyRate } from '../../../shared/utils/utils';
@Component({
  selector: 'app-box-fluencyrate',
  templateUrl: './box-fluencyrate.component.html',
  standalone: false,
})
export class BoxFluencyrateComponent extends BaseBoxComponent implements AfterViewInit, OnInit {
  text: string | undefined = '-';
   expectedAmount: number = 0;
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() target: PatientTargetModel | undefined = undefined;
  @Input() isPreview?: boolean;
  timerInitialValue: number = 60;
  targetID: number = 0;
  targetData: PatientTargetDataModel[] = [];

  isStarted: boolean = false;
  timerValue: number = 0;
  private startTime: number = 0;
  fluencies: { totalCorrect: number; totalIncorrect: number; RecordedDate: string; FormattedDate: string; rate: number }[] = [];  
  totalCorrect: number = 0;
  totalIncorrect: number = 0;
  timeOut: any;
  remainingTrials: number = 0;

  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    effect(() => {
      this.patientSessionService.dataChanged();
      const targetData = this.patientSessionService.getTargetData(this.targetID);
      if (targetData) {
        this.targetData = targetData.filter(t => !t.DeletedDate);
        this.remainingTrials = (this.target?.DesiredTrials ?? 0) - this.targetData.length;
        if (this.remainingTrials < 0) {
          this.remainingTrials = 0;
        }

        if (this.target?.TargetStatus === PatientTargetStatus.Mastered || this.target?.TargetStatus === PatientTargetStatus.Closed || this.target?.TargetStatus === PatientTargetStatus.Fluent) {
          this.remainingTrials = 0;
        }
        
        this.fluencies = this.targetData.map(t => {
          if (!t.TextValue) {
            t.TextValue = '';
          }
          //parse the totalCorrect and totalIncorrect from the textvalue
          const [totalCorrect, totalIncorrect] = t.TextValue.split('/').map(Number);
          return {
            totalCorrect: totalCorrect,
            totalIncorrect: totalIncorrect,
              RecordedDate: t.RecordedDate ?? '',
            FormattedDate: this.formatDate(new Date(t.RecordedDate ?? '')),
            rate: ((totalCorrect + totalIncorrect) <= 0) ? 0 : (100 * totalCorrect / (totalCorrect + totalIncorrect))
          };

        });

      }
      else {
        this.targetData = [];
        this.fluencies = [];
      }
    });

    effect(() => {
      const targetsRunningData = this.patientSessionService.getTargetsRunningData(this.patientSessionService.getPatientSession()?.ID ?? -1,this.targetID);
      if (targetsRunningData) {
        this.isStarted = targetsRunningData.Data.isStarted;
        this.startTime = targetsRunningData.Data.startTime;
        this.timerValue = this.isStarted
          ? this.getCurrentTimerValue()
          : targetsRunningData.Data.timerValue;
        this.totalCorrect = targetsRunningData.Data.totalCorrect;
        this.totalIncorrect = targetsRunningData.Data.totalIncorrect;
        if (this.isStarted) {
          //Use set timeout just to avoid saving it inside an effect
          if (this.timeOut) {
            clearTimeout(this.timeOut);
          }
          this.timeOut = setTimeout(() => {
            this.updateTimer();
          }, 10);
        }
      } else {
        this.isStarted = false;
        this.startTime = 0;
        this.timerValue = this.getInitialTimerValue();
        this.totalCorrect = 0;
        this.totalIncorrect = 0;
      }
    });
  }

  ngOnInit() {
    this.text = this.target?.TargetName ?? '-';
    this.targetID = this.target?.ID ?? 0;
    this.expectedAmount = this.target?.DesiredTrials ?? 1;
    this.timerInitialValue = this.isOpenEndedFluencyRate()
      ? 0
      : this.target?.Duration ?? 60;
    this.timerValue = this.getInitialTimerValue();
  }

  override doLongPress(): void {
    this.showExtra2();
  }

  startTimer() {
    if (this.isStarted) {
      this.showExtra();
    } else {
      this.isStarted = true;
      this.startTime = Date.now();
      this.totalCorrect = 0;
      this.totalIncorrect = 0;
      this.updateTimer();
      this.saveTargetRunningData();
      this.showExtra();
    }
  }

  openFluencyRate() {
    if (this.isStarted) {
      this.showExtra();
    } 
  }

  saveTargetRunningData() {
    this.patientSessionService.setTargetsRunningData(this.patientSessionService.getPatientSession()?.ID ?? -1, this.targetID, { isFluencyRateTarget:true, isOpenEndedFluencyRate: this.isOpenEndedFluencyRate(), isStarted: this.isStarted, startTime: this.startTime, timerValue: this.timerValue, totalCorrect: this.totalCorrect, totalIncorrect: this.totalIncorrect });
  }

  private updateTimer(): void {
    if (this.isStarted) {
      this.timerValue = this.getCurrentTimerValue();

      if (this.isOpenEndedFluencyRate()) {
        this.scheduleNextTimerUpdate();
        return;
      }

      const shouldRingAlarm = this.timerValue === 0 &&
        (this.target?.AlarmOption === AlarmOption.AlarmAtEnd);
      
      this.ringAlarmIfNeeded(
        this.target?.AlarmOption ?? AlarmOption.NoAlarm, 
        this.timerValue, 
        this.target?.Duration ?? 0, 
        this.target?.AlarmSoundOption ?? 1,
        false
      );

      if (this.timerValue <= 0) {
        // If alarm should ring, delay the stop to allow alarm to play
        if (shouldRingAlarm) {
          setTimeout(() => {
            this.stopTimer();
          }, 100);
        } else {
          this.stopTimer();
        }
      } else {
        this.scheduleNextTimerUpdate();
      }
    }else {
      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }
    }
  }

  private getCurrentTimerValue(): number {
    const elapsedSeconds = this.getElapsedSeconds();
    if (this.isOpenEndedFluencyRate()) {
      return elapsedSeconds;
    }

    return Math.max(
      0,
      this.timerInitialValue - elapsedSeconds
    );
  }

  private getElapsedSeconds(): number {
    if (!this.startTime) {
      return 0;
    }

    return Math.max(0, Math.floor((Date.now() - this.startTime) / 1000));
  }

  private getInitialTimerValue(): number {
    return this.isOpenEndedFluencyRate() ? 0 : this.timerInitialValue;
  }

  private isOpenEndedFluencyRate(): boolean {
    return targetIsOpenEndedFluencyRate(this.target);
  }

  private scheduleNextTimerUpdate(): void {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    this.timeOut = setTimeout(() => {
      this.updateTimer();
      this.saveTargetRunningData();
    }, 1000);
  }

  override ngOnDestroy() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    super.ngOnDestroy();
  }

  stopTimer() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    const elapsedSeconds = this.getElapsedSeconds();
    if (elapsedSeconds > 0 || this.totalCorrect > 0 || this.totalIncorrect > 0) {
      const currentDate = new Date();
      const formattedDate = this.formatDate(currentDate);
      const total = this.totalCorrect + this.totalIncorrect;
      const rate = 100 * this.totalCorrect / total;
      this.patientSessionService.addTargetData({
        IntValue: this.totalCorrect,
        TextValue: `${this.totalCorrect}/${this.totalIncorrect}`,
        Duration: elapsedSeconds,
        PatientTargetID: this.targetID,
        RecordedDate: currentDate.toServerString(),
      });
    }
    this.timerValue = this.getInitialTimerValue();
    this.isStarted = false;
    this.totalCorrect = 0;
    this.totalIncorrect = 0;
    this.startTime = 0;

    this.saveTargetRunningData();
    this.closeExtra();
  }


  timerString() {
    const minutes = Math.floor(this.timerValue / 60);
    const seconds = this.timerValue % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  lastFluency() {
    if (this.fluencies.length > 0) {
      const lastFluency = this.fluencies[this.fluencies.length - 1];
      return `${lastFluency.totalCorrect} / ${lastFluency.rate.toFixed(0)}%`;
    } else {
      return '';
    }
  }

  incrementCorrect() {
    this.totalCorrect++;
    this.saveTargetRunningData();
  }

  incrementIncorrect() {
    this.totalIncorrect++;
    this.saveTargetRunningData();
  }

  deleteData(data?: any) {
    if (data) {
      //confirm the deletion
      const confirmed = this.alertService.confirm('Are you sure you want to delete this data?', 'Delete Data');
      confirmed.subscribe(result => {
        if (result) {
          let targetDataToSave: PatientTargetDataModel | undefined = this.targetData.find(t =>
            t.RecordedDate === data.RecordedDate
          );

          if (targetDataToSave) {
            targetDataToSave.DeletedDate = new Date().toServerString();
            this.patientSessionService.saveTargetData(targetDataToSave);  
          }
        }
      });
    }
  }
}
